import React from 'react'
import { cx } from '@emotion/css'
import { Box, Flex, Checkbox } from 'front-end/src/components'
import { HighlightMatchedString } from 'front-end/src/components/common/selectEntitiesForm/HighlightMatchedString'
import { partition } from 'lodash'
import { IconArrowDown } from 'front-end/src/components/common/icon/IconArrowDown'
import { useDisclosure } from '@chakra-ui/react'
import IconArrowRight from 'front-end/src/components/common/icon/IconArrowRight'
import { MatchedSearchableItem } from 'front-end/src/components/common/selectEntitiesForm'
import { MAIN_SEARCHABLE_ITEM_TEXT_PROPERTY_KEY } from 'front-end/src/components/common/selectEntitiesForm/FuzzyResourcesFilter'
import IconSubsection from 'front-end/src/components/common/icon/IconSubsection'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import { EventModelAsStudent } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useEventSelectorContext } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/event-modal/event-selector/EventSelectorContext'

type EventSelectorRowItemItemProps = {
  matchedItem: MatchedSearchableItem
  defaultIsOpen: boolean
  occurrences?: MatchedSearchableItem[]
}

export const EventSelectorRowItem: React.FC<EventSelectorRowItemItemProps> = ({
  matchedItem,
  defaultIsOpen,
  occurrences
}) => {
  const { selectedEvents, changeSelectedEvents } = useEventSelectorContext()
  const isRecurringEvent =
    matchedItem.item.type === 'RecurringEventModelAsAdmin'

  const event = matchedItem.item.original as unknown as EventModelAsStudent
  const isOccurrence = !!event.recurring_event_id

  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen })

  const [matchedMainText] = React.useMemo(() => {
    return partition(
      matchedItem.matches,
      (m) => m.key === MAIN_SEARCHABLE_ITEM_TEXT_PROPERTY_KEY
    ).map((ms) => ms.map((m) => m.indices))
  }, [matchedItem.matches])

  const isEventSelected = () => {
    if (
      isOccurrence &&
      selectedEvents['recEvents'].includes(event.recurring_event_id as number)
    ) {
      return true
    }
    const key = isRecurringEvent ? 'recEvents' : 'events'
    return selectedEvents[key].includes(matchedItem.item.id)
  }

  const isEventPartiallySelected = () => {
    if (isRecurringEvent) {
      const occurrenceIds = occurrences?.map(
        (occurrence) => occurrence.item.original.id
      )
      if (!isSelected) {
        return occurrenceIds?.some((oid) => selectedEvents.events.includes(oid))
      }
    }
    return false
  }

  const isSelected = isEventSelected()

  return (
    <>
      <Box
        key={`${matchedItem.item.type}-${matchedItem.item.id}`}
        className={cx('List__Row', {
          active: isSelected
        })}
        h="52px"
        align="center"
        userSelect="none"
        onClick={(e) => {
          e.preventDefault()
          changeSelectedEvents(matchedItem.item.id, isRecurringEvent)
        }}
      >
        <Flex alignItems="center" width="100%">
          {isOccurrence && (
            <IconSubsection ml="30px" mr="6px" color="gray.400" />
          )}
          <Checkbox
            isIndeterminate={isEventPartiallySelected()}
            aria-label="Selected"
            isChecked={isSelected}
          />

          {!isOccurrence && (
            <Flex
              align="center"
              ml="12px"
              w="16px"
              onClick={(e) => {
                e.stopPropagation()
                onToggle()
              }}
            >
              {isRecurringEvent && (
                <>
                  {isOpen ? (
                    <IconArrowDown />
                  ) : (
                    <IconArrowRight fontSize="17px" />
                  )}
                </>
              )}
            </Flex>
          )}
          <Flex
            textAlign="left"
            marginLeft="12px"
            overflow="hidden"
            overflowWrap="normal"
            justify="center"
            direction="column"
          >
            <HighlightMatchedString
              text={matchedItem.item.text}
              matches={matchedMainText}
            />
            {event.formattedTimeRange && (
              <Box
                fontSize={fontSizes.sm}
                textColor="grey.mid"
                whiteSpace="nowrap"
              >
                {event.formattedTimeRange}
              </Box>
            )}
          </Flex>
        </Flex>
      </Box>

      {occurrences && isOpen && (
        <>
          {occurrences.map((occurrence) => {
            return (
              <EventSelectorRowItem
                key={`${occurrence.item.type}-${occurrence.item.id}`}
                matchedItem={occurrence}
                defaultIsOpen={false}
              />
            )
          })}
        </>
      )}
    </>
  )
}
